import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { ListChecks, ShieldAlert, Wallet, ScrollText, LogOut, Lock, MonitorSmartphone } from "lucide-react";
import BrandMark from "./BrandMark";
import {
  getPeopleEngineManager,
  getPeopleEngineCapabilities,
  clearPeopleEngineSession,
} from "../lib/api";

/**
 * People's Engine shell — distinct from Control Room.
 *
 * Truth contract:
 *   Presence captures evidence.
 *   People's Engine decides workforce exceptions.   ← this app
 *   Control Room authorises access.
 *   Payroll consumes only ELIGIBLE finalised records.
 */

const NAV = [
  {
    to: "/people/reviews",
    label: "Review Queue",
    icon: ListChecks,
    capability: "attendance.review.approve",
    testid: "pe-nav-reviews",
  },
  {
    to: "/people/overrides",
    label: "Manual Override",
    icon: ShieldAlert,
    capability: "attendance.override.create",
    testid: "pe-nav-overrides",
  },
  {
    to: "/people/payroll",
    label: "Payroll Eligibility",
    icon: Wallet,
    capability: "payroll.finalize",
    testid: "pe-nav-payroll",
  },
  {
    to: "/people/devices",
    label: "Presence Devices",
    icon: MonitorSmartphone,
    capability: null,
    testid: "pe-nav-devices",
  },
  {
    to: "/people/audit",
    label: "Audit Ledger",
    icon: ScrollText,
    capability: null,
    testid: "pe-nav-audit",
  },
];

export default function PeopleLayout({ children, title, subtitle, actions }) {
  const nav = useNavigate();
  const manager = getPeopleEngineManager();
  const caps = getPeopleEngineCapabilities();

  const logout = () => {
    clearPeopleEngineSession();
    nav("/people/login", { replace: true });
  };

  return (
    <div className="people-root min-h-screen bg-[#0F1115] text-neutral-100">
      <header className="border-b border-neutral-800 bg-[#181B22]">
        <div className="flex items-center h-14 px-5 gap-6">
          <button
            onClick={() => nav("/people/reviews")}
            className="flex items-center gap-3"
            data-testid="pe-brand-home"
          >
            <BrandMark size={26} inverted />
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-amber-300">
              People&apos;s Engine
            </span>
          </button>
          <div className="h-5 w-px bg-neutral-700" />
          <div className="font-mono text-xs text-neutral-400 uppercase tracking-wider">
            HR · Workforce Decisions
          </div>
          <div className="ml-auto flex items-center gap-4">
            <span
              data-testid="pe-fresh-session"
              className="hidden md:inline-flex items-center gap-1 px-2 py-1 border border-amber-500 text-amber-300 font-mono text-[10px] uppercase tracking-wider"
            >
              <Lock size={11} /> Fresh session · sessionStorage only
            </span>
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-400">
                {manager?.role || "—"}
              </div>
              <div
                className="text-xs font-medium text-neutral-100"
                data-testid="pe-current-user"
              >
                {manager?.name || manager?.email || "Unknown"}
              </div>
            </div>
            <button
              data-testid="pe-btn-logout"
              onClick={logout}
              className="h-9 px-3 border border-neutral-700 hover:border-amber-400 hover:text-amber-300 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-2"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-12 min-h-[calc(100vh-56px)]">
        <aside className="col-span-12 lg:col-span-2 border-r border-neutral-800 bg-[#13161C] py-4">
          <nav className="flex flex-col">
            {NAV.map((n) => {
              const Icon = n.icon;
              const allowed = n.capability == null || caps.includes(n.capability);
              return (
                <NavLink
                  key={n.to}
                  to={allowed ? n.to : "#"}
                  data-testid={n.testid}
                  aria-disabled={!allowed}
                  onClick={(e) => {
                    if (!allowed) e.preventDefault();
                  }}
                  className={({ isActive }) =>
                    `flex items-center justify-between gap-3 px-5 py-3 border-l-2 transition-colors text-sm ${
                      !allowed
                        ? "border-l-transparent text-neutral-600 cursor-not-allowed"
                        : isActive
                        ? "border-l-amber-400 bg-[#0F1115] text-amber-300 font-semibold"
                        : "border-l-transparent text-neutral-400 hover:bg-[#1A1E26] hover:text-neutral-100"
                    }`
                  }
                >
                  <span className="inline-flex items-center gap-3">
                    <Icon size={16} strokeWidth={1.75} />
                    {n.label}
                  </span>
                  {!allowed && (
                    <Lock size={11} className="text-neutral-600" data-testid={`${n.testid}-locked`} />
                  )}
                </NavLink>
              );
            })}
          </nav>
          <div className="mt-6 px-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-neutral-500 mb-2">
              Capabilities
            </div>
            <div className="space-y-1">
              {caps.length === 0 && (
                <div className="font-mono text-[11px] text-neutral-500" data-testid="pe-caps-empty">
                  monitoring-only
                </div>
              )}
              {caps.map((c) => (
                <div
                  key={c}
                  className="font-mono text-[10px] text-amber-300/80 truncate"
                  data-testid={`pe-cap-${c}`}
                >
                  ✓ {c}
                </div>
              ))}
            </div>
            <div className="mt-6 border border-neutral-800 bg-[#0F1115] p-3">
              <div className="font-mono text-[10px] uppercase tracking-wider text-neutral-500">
                Truth contract
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-neutral-400 font-mono">
                Presence captures. People&apos;s Engine decides. Control Room authorises. Payroll consumes only ELIGIBLE.
              </p>
            </div>
          </div>
        </aside>

        <main className="col-span-12 lg:col-span-10 bg-[#0F1115]">
          <div className="px-6 py-5 border-b border-neutral-800 flex items-end justify-between gap-4">
            <div>
              <h1
                className="font-heading text-2xl font-black tracking-tighter text-neutral-100"
                data-testid="pe-page-title"
              >
                {title}
              </h1>
              {subtitle && (
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-neutral-500 mt-1">
                  {subtitle}
                </div>
              )}
            </div>
            <div>{actions}</div>
          </div>
          <div className="p-6">{children}</div>
        </main>
      </div>
    </div>
  );
}
